/**
 * Reference data for MITRE ATT&CK for ICS - the 12 tactics and a curated set
 * of techniques. Used to render alert mappings and to show coverage of
 * detection rules across the kill chain.
 */
export interface MitreTechnique {
  technique_id: string;
  name: string;
  description?: string;
}

export interface MitreTactic {
  tactic_id: string;
  name: string;
  description: string;
  techniques: MitreTechnique[];
}

export const MITRE_ICS_TACTICS: MitreTactic[] = [
  {
    tactic_id: 'TA0108',
    name: 'Initial Access',
    description: 'Adversary trying to get into the ICS environment.',
    techniques: [
      { technique_id: 'T0817', name: 'Drive-by Compromise' },
      { technique_id: 'T0819', name: 'Exploit Public-Facing Application' },
      { technique_id: 'T0822', name: 'External Remote Services' },
      { technique_id: 'T0865', name: 'Spearphishing Attachment' },
      { technique_id: 'T0862', name: 'Supply Chain Compromise' },
      { technique_id: 'T0818', name: 'Engineering Workstation Compromise' },
    ],
  },
  {
    tactic_id: 'TA0104',
    name: 'Execution',
    description: 'Adversary trying to run malicious code.',
    techniques: [
      { technique_id: 'T0807', name: 'Command-Line Interface' },
      { technique_id: 'T0871', name: 'Execution through API' },
      { technique_id: 'T0823', name: 'Graphical User Interface' },
      { technique_id: 'T0863', name: 'User Execution' },
    ],
  },
  {
    tactic_id: 'TA0110',
    name: 'Persistence',
    description: 'Adversary trying to maintain their foothold.',
    techniques: [
      { technique_id: 'T0859', name: 'Valid Accounts' },
      { technique_id: 'T0857', name: 'System Firmware' },
      { technique_id: 'T0839', name: 'Module Firmware' },
    ],
  },
  {
    tactic_id: 'TA0111',
    name: 'Privilege Escalation',
    description: 'Adversary trying to gain higher-level permissions.',
    techniques: [
      { technique_id: 'T0890', name: 'Exploitation for Privilege Escalation' },
      { technique_id: 'T0874', name: 'Hooking' },
    ],
  },
  {
    tactic_id: 'TA0103',
    name: 'Evasion',
    description: 'Adversary trying to avoid detection.',
    techniques: [
      { technique_id: 'T0858', name: 'Change Operating Mode' },
      { technique_id: 'T0851', name: 'Rootkit' },
      { technique_id: 'T0872', name: 'Indicator Removal on Host' },
    ],
  },
  {
    tactic_id: 'TA0102',
    name: 'Discovery',
    description: 'Adversary trying to figure out the environment.',
    techniques: [
      { technique_id: 'T0840', name: 'Network Connection Enumeration' },
      { technique_id: 'T0888', name: 'Remote System Information Discovery' },
      { technique_id: 'T0846', name: 'Remote System Discovery' },
    ],
  },
  {
    tactic_id: 'TA0109',
    name: 'Lateral Movement',
    description: 'Adversary trying to move through the environment.',
    techniques: [
      { technique_id: 'T0866', name: 'Exploitation of Remote Services' },
      { technique_id: 'T0867', name: 'Lateral Tool Transfer' },
      { technique_id: 'T0859', name: 'Valid Accounts' },
    ],
  },
  {
    tactic_id: 'TA0100',
    name: 'Collection',
    description: 'Adversary trying to gather data of interest.',
    techniques: [
      { technique_id: 'T0830', name: 'Adversary-in-the-Middle' },
      { technique_id: 'T0810', name: 'Data from Information Repositories' },
      { technique_id: 'T0856', name: 'Spoof Reporting Message' },
    ],
  },
  {
    tactic_id: 'TA0101',
    name: 'Command and Control',
    description: 'Adversary trying to communicate with compromised systems.',
    techniques: [
      { technique_id: 'T0869', name: 'Standard Application Layer Protocol' },
      { technique_id: 'T0883', name: 'Internet Accessible Device' },
    ],
  },
  {
    tactic_id: 'TA0107',
    name: 'Inhibit Response Function',
    description: 'Adversary trying to prevent safety, protection, or quality response.',
    techniques: [
      { technique_id: 'T0814', name: 'Denial of Service' },
      { technique_id: 'T0815', name: 'Denial of View' },
      { technique_id: 'T0827', name: 'Loss of Control' },
      { technique_id: 'T0837', name: 'Loss of Protection' },
      { technique_id: 'T0879', name: 'Damage to Property' },
    ],
  },
  {
    tactic_id: 'TA0106',
    name: 'Impair Process Control',
    description: 'Adversary trying to manipulate, disable, or damage physical processes.',
    techniques: [
      { technique_id: 'T0806', name: 'Brute Force I/O' },
      { technique_id: 'T0836', name: 'Modify Parameter' },
      { technique_id: 'T0809', name: 'Data Destruction' },
      { technique_id: 'T0856', name: 'Spoof Reporting Message' },
      { technique_id: 'T0855', name: 'Unauthorized Command Message' },
    ],
  },
  {
    tactic_id: 'TA0105',
    name: 'Impact',
    description: 'Adversary trying to manipulate, interrupt, or destroy systems and data.',
    techniques: [
      { technique_id: 'T0826', name: 'Loss of Availability' },
      { technique_id: 'T0827', name: 'Loss of Control' },
      { technique_id: 'T0829', name: 'Loss of View' },
      { technique_id: 'T0831', name: 'Manipulation of Control' },
      { technique_id: 'T0832', name: 'Manipulation of View' },
      { technique_id: 'T0879', name: 'Damage to Property' },
      { technique_id: 'T1486', name: 'Data Encrypted for Impact' },
    ],
  },
];

export function findTechnique(techniqueId: string):
  | { tactic: MitreTactic; technique: MitreTechnique }
  | undefined {
  for (const t of MITRE_ICS_TACTICS) {
    const found = t.techniques.find((tech) => tech.technique_id === techniqueId);
    if (found) return { tactic: t, technique: found };
  }
  return undefined;
}
